import {ArrowRight, Mail, Phone, MapPin, Clock, CheckCircle2, Sparkles, Calendar, Lock} from 'lucide-react';
import {useState} from 'react';
import axios from "axios";
import {HOST} from "../constants";

export function ContactCTA() {
    const [loading, setLoading] = useState(false)
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        description: '',
    });

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true)
        await axios.post(HOST + "/contact", formData)
        setLoading(false)
        alert('Thank you for your interest! Our team will reach out within 2 hours during business hours.');
        setFormData({name: '', email: '', phone: '', description: ''});
    };

    const handleChange = (field: string, value: string) => {
        setFormData((prev) => ({...prev, [field]: value}));
    };

    return (
        <section id="contact"
                 className="py-20 md:py-32 relative overflow-hidden bg-gradient-to-b from-white via-gray-50 to-white">
            <div
                className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-br from-blue-600/5 to-transparent rounded-full blur-3xl"/>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                {/* Urgency Banner */}
                <div className="max-w-4xl mx-auto mb-12 animate-fadeInUp">
                    <div
                        className="bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-200 rounded-2xl p-6 text-center">
                        <div className="flex items-center justify-center gap-2 mb-2">
                            <Clock size={20} className="text-amber-600"/>
                            <span className="text-sm font-bold text-amber-900 uppercase tracking-wider">Limited Availability</span>
                        </div>
                        <p className="text-gray-700 font-medium">
                            We're accepting only <span
                            className="font-bold text-amber-700">3 new clients this quarter</span> to ensure exceptional
                            delivery. Book your free strategy session before spots fill up.
                        </p>
                    </div>
                </div>

                <div className="grid lg:grid-cols-2 gap-16">
                    {/* Left - Enhanced CTA Content */}
                    <div className="animate-fadeInUp">
                        <div
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-blue-600/10 via-purple-500/10 to-cyan-500/10 border border-blue-200/50 mb-6">
                            <Sparkles size={16} className="text-blue-600"/>
                            <span className="text-sm text-blue-700 font-semibold uppercase tracking-wider">
                Let's Talk
              </span>
                        </div>

                        <h2 className="text-4xl md:text-6xl font-extrabold mb-6 leading-tight">
                            <span className="gradient-text-premium">Ready to 10x</span>
                            <br/>
                            <span className="text-gray-900">Your Growth?</span>
                        </h2>

                        <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                            Let's discuss how ZeeJay Labs can help you dominate your market. In our free 45-minute
                            strategy session, we'll:
                        </p>

                        {/* What You'll Get */}
                        <div className="space-y-4 mb-10">
                            {[
                                'Analyze your biggest technical challenges and growth bottlenecks',
                                'Show you exactly how we\'d solve them (real solutions, not sales pitches)',
                                'Provide a detailed roadmap with timeline and ROI projections',
                                'Give you actionable insights you can use—even if we never work together'
                            ].map((item, idx) => (
                                <div key={idx} className="flex items-start gap-3">
                                    <div
                                        className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                                        <CheckCircle2 size={16} className="text-green-600"/>
                                    </div>
                                    <span className="text-gray-700 font-medium leading-relaxed">{item}</span>
                                </div>
                            ))}
                        </div>

                        {/* Contact Info */}
                        <div className="space-y-5 mb-10 p-6 bg-gray-50 rounded-2xl border border-gray-200">
                            <h3 className="font-bold text-gray-900 mb-4">Prefer to talk directly?</h3>

                            <div className="flex items-center gap-4">
                                <div
                                    className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600 to-purple-600 text-white flex items-center justify-center shadow-lg">
                                    <Mail size={20}/>
                                </div>
                                <div>
                                    <div className="text-xs text-gray-600 mb-0.5 font-medium">Email</div>
                                    <a href="mailto:contact@zeejaylabs.dev"
                                       className="font-bold text-gray-900 hover:text-blue-600 transition-colors">
                                        contact@zeejaylabs.com
                                    </a>
                                </div>
                            </div>

                            <div className="flex items-center gap-4">
                                <div
                                    className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600 to-purple-600 text-white flex items-center justify-center shadow-lg">
                                    <Phone size={20}/>
                                </div>
                                <div>
                                    <div className="text-xs text-gray-600 mb-0.5 font-medium">Phone</div>
                                    <a href="tel:+237654849481"
                                       className="font-bold text-gray-900 hover:text-blue-600 transition-colors">
                                        +237 654 849 481
                                    </a>
                                </div>
                            </div>

                            <div className="flex items-center gap-4">
                                <div
                                    className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600 to-purple-600 text-white flex items-center justify-center shadow-lg">
                                    <MapPin size={20}/>
                                </div>
                                <div>
                                    <div className="text-xs text-gray-600 mb-0.5 font-medium">Headquarters</div>
                                    <div className="font-bold text-gray-900">Bonaberi, Douala - Cameroon</div>
                                </div>
                            </div>
                        </div>

                        {/* Trust Signals */}
                        <div className="grid grid-cols-3 gap-4">
                            {[
                                {value: '< 2hrs', label: 'Response Time'},
                                {value: '100%', label: 'Free Consultation'},
                                {value: 'No Risk', label: 'No Obligation'}
                            ].map((item, idx) => (
                                <div key={idx}
                                     className="text-center p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
                                    <div className="text-lg font-bold gradient-text-primary mb-1">{item.value}</div>
                                    <div className="text-xs text-gray-600 font-medium">{item.label}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Right - Enhanced Contact Form */}
                    <div className="animate-fadeInUp" style={{animationDelay: '0.2s'}}>
                        <form
                            onSubmit={handleSubmit}
                            className="premium-card p-10 rounded-3xl shadow-premium-lg"
                        >
                            <div className="flex items-center justify-between mb-8">
                                <h3 className="text-3xl font-extrabold text-gray-900">Get Started Now</h3>
                                <div className="flex items-center gap-2 px-3 py-1.5 bg-green-100 rounded-full">
                                    <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"/>
                                    <span className="text-xs font-bold text-green-700">We're Online</span>
                                </div>
                            </div>

                            <div className="space-y-6">
                                <div>
                                    <label htmlFor="name" className="block text-sm font-bold text-gray-900 mb-3">
                                        Full Name *
                                    </label>
                                    <input
                                        id="name"
                                        type="text"
                                        required
                                        placeholder="John Smith"
                                        value={formData.name}
                                        onChange={(e) => handleChange('name', e.target.value)}
                                        className="w-full px-5 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-blue-600 outline-none transition-all bg-white hover:border-gray-300"
                                    />
                                </div>

                                <div>
                                    <label htmlFor="email" className="block text-sm font-bold text-gray-900 mb-3">
                                        Work Email *
                                    </label>
                                    <input
                                        id="email"
                                        type="email"
                                        required
                                        placeholder="john@company.com"
                                        value={formData.email}
                                        onChange={(e) => handleChange('email', e.target.value)}
                                        className="w-full px-5 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-blue-600 outline-none transition-all bg-white hover:border-gray-300"
                                    />
                                </div>

                                <div>
                                    <label htmlFor="company" className="block text-sm font-bold text-gray-900 mb-3">
                                        Phone *
                                    </label>
                                    <input
                                        id="company"
                                        type="tel"
                                        required
                                        placeholder="123654789"
                                        value={formData.phone}
                                        onChange={(e) => handleChange('phone', e.target.value)}
                                        className="w-full px-5 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-blue-600 outline-none transition-all bg-white hover:border-gray-300"
                                    />
                                </div>

                                <div>
                                    <label htmlFor="message" className="block text-sm font-bold text-gray-900 mb-3">
                                        Tell us about your project *
                                    </label>
                                    <textarea
                                        id="message"
                                        required
                                        rows={5}
                                        placeholder="What challenges are you facing? What are your goals?"
                                        value={formData.description}
                                        onChange={(e) => handleChange('description', e.target.value)}
                                        className="w-full px-5 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-blue-600 outline-none transition-all resize-none bg-white hover:border-gray-300"
                                    />
                                </div>

                                <button
                                    type="submit"
                                    style={{backgroundColor: "#5743ff"}}
                                    disabled={loading}
                                    className="group w-full px-8 py-5 text-white rounded-xl hover:from-blue-700 hover:via-blue-800 hover:to-purple-800 transition-all hover:shadow-2xl hover:shadow-blue-500/50 hover:-translate-y-1 flex items-center justify-center gap-3 font-bold text-lg relative overflow-hidden"
                                >
                                    <Calendar size={22}/>
                                    <span
                                        className="relative z-10">{loading ? "Sending..." : "Book Your Free Strategy Session"}</span>
                                    <ArrowRight size={22}
                                                className="relative z-10 group-hover:translate-x-1 transition-transform"/>
                                    <div
                                        className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-600 opacity-0 group-hover:opacity-100 transition-opacity"/>
                                </button>
                            </div>

                            {/* Trust Badge */}
                            <div className="mt-6 p-4 rounded-xl bg-blue-50 border border-blue-100 ">
                                <p className="text-sm text-gray-700 leading-relaxed text-center">
                                    <div style={{
                                        display: "flex",
                                        justifyContent: "center",
                                        gap: 10,
                                        alignItems: "center"
                                    }}>
                                        <Lock style={{width: 20}}/>
                                        <span className="font-semibold">Your information is secure</span> and will never
                                        be shared. Average response time: <span className="font-bold text-blue-600">under 2 hours</span>.
                                    </div>
                                </p>
                            </div>
                        </form>
                    </div>
                </div>

                {/* Social Proof Banner */}
                <div className="mt-20 text-center animate-fadeInUp">
                    <div
                        className="inline-block p-8 md:p-10 rounded-3xl bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white shadow-premium-lg border border-gray-700 max-w-4xl">
                        <div className="flex items-center justify-center gap-2 mb-4">
                            {[...Array(5)].map((_, i) => (
                                <div key={i} className="text-amber-400 fill-amber-400">★</div>
                            ))}
                        </div>
                        <p className="text-xl md:text-2xl font-medium mb-2 italic">
                            "The best technology investment we've ever made. Period."
                        </p>
                        <p className="text-sm text-gray-400">
                            — Sarah Chen, CTO at FinanceFlow (Generated $15M in new revenue)
                        </p>
                    </div>
                </div>

                {/* Footer */}
                <div className="mt-24 pt-12 border-t-2 border-gray-200 text-center animate-fadeInUp">
                    <h4 className="text-2xl font-extrabold gradient-text-premium mb-3">
                        <div style={{width: '100%', display: "flex", justifyContent: "center"}}>
                            <img width={200} height={"auto"} src={require("../assets/full_logo_transparent.png")}
                                 alt={"ZeeJay Labs Logo"}/>
                        </div>
                    </h4>
                    <p className="text-sm text-gray-600 mb-4">
                        Elite Technology Consulting · Transforming Businesses Since 2015
                    </p>
                    <p className="text-xs text-gray-500">
                        © 2026 ZeeJay Labs. All rights reserved.
                    </p>
                </div>
            </div>
        </section>
    );
}